import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ResendButton({ kind, queueId, onDone, language }) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const t = (hi, en) => (language === 'hi' ? hi : en);

  const resend = async () => {
    if (!queueId || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const res = await fetch(`${API_URL}/api/conversion-proof/resend/${kind}/${encodeURIComponent(queueId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        setFeedback({ ok: true, msg: t('भेज दिया', 'Sent') });
      } else {
        setFeedback({ ok: false, msg: data?.detail || data?.error || t('Resend विफल', 'Resend failed') });
      }
      if (onDone) onDone();
    } catch (e) {
      setFeedback({ ok: false, msg: e?.message || 'Error' });
    } finally {
      setBusy(false);
    }
  };

  if (!queueId) return null;
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={resend}
        disabled={busy}
        className="px-2 py-1 text-xs font-semibold rounded-md bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 disabled:opacity-50 transition"
        data-testid={`resend-${kind}-${queueId}`}
      >
        {busy ? t('भेज रहा...', 'Sending...') : `🔄 ${t('Resend', 'Resend')}`}
      </button>
      {feedback && (
        <span
          className={`text-[11px] font-semibold ${feedback.ok ? 'text-emerald-600' : 'text-rose-600'}`}
          data-testid={`resend-feedback-${kind}-${queueId}`}
        >
          {feedback.ok ? '✓ ' : '✗ '}
          {feedback.msg}
        </span>
      )}
    </div>
  );
}

export const ConversionProofPanel = ({ data, language, onRefresh }) => {
  const loading = !data;

  if (loading) return <div className="rounded-2xl bg-white p-6 shadow-sm" data-testid="conversion-proof-loading">Loading proof panel...</div>;

  const cards = [
    { id: 'demo-email', label: 'Demo Email', value: data.proof_counts?.demo_email_sent || 0, color: 'bg-blue-50 text-blue-700' },
    { id: 'demo-whatsapp', label: 'Demo WhatsApp', value: data.proof_counts?.demo_whatsapp_sent || 0, color: 'bg-green-50 text-green-700' },
    { id: 'payment-email', label: 'Payment Email', value: data.proof_counts?.payment_email_sent || 0, color: 'bg-purple-50 text-purple-700' },
    { id: 'payment-whatsapp', label: 'Payment WhatsApp', value: data.proof_counts?.payment_whatsapp_sent || 0, color: 'bg-orange-50 text-orange-700' },
    { id: 'recovery-email', label: 'Recovery Email', value: data.proof_counts?.recovery_email_sent || 0, color: 'bg-emerald-50 text-emerald-700' },
    { id: 'recovery-whatsapp', label: 'Recovery WhatsApp', value: data.proof_counts?.recovery_whatsapp_sent || 0, color: 'bg-amber-50 text-amber-700' },
  ];

  return (
    <div className="space-y-6" data-testid="conversion-proof-panel">
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-600 p-6 text-white shadow-lg">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">Conversion Proof</p>
        <h3 className="mt-2 text-3xl font-black">{language === 'hi' ? 'SMTP Inbox-to-Dashboard Proof Panel' : 'SMTP Inbox-to-Dashboard Proof Panel'}</h3>
        <p className="mt-2 text-sm text-white/85" data-testid="conversion-proof-copy">
          {language === 'hi' ? 'Live SMTP, WhatsApp, CTA funnel, payment provisioning aur recovery flows ka proof yahin dikhega.' : 'Live SMTP, WhatsApp, CTA funnel, payment provisioning, and recovery flow proof appears here.'}
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-smtp-card">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h4 className="text-lg font-bold text-slate-900">SMTP Live Status</h4>
            <p className="text-sm text-slate-500">{data.smtp?.email} via {data.smtp?.host}:{data.smtp?.port}</p>
          </div>
          <span className="rounded-full bg-green-100 px-4 py-2 text-sm font-bold text-green-700" data-testid="conversion-proof-smtp-live-badge">
            {data.smtp?.configured ? 'LIVE' : 'NOT CONFIGURED'}
          </span>
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Last Test: {data.smtp?.last_test || 'N/A'} · {data.smtp?.last_test_at || 'N/A'}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="conversion-proof-count-cards">
        {cards.map((card) => (
          <div key={card.id} className={`rounded-2xl p-4 ${card.color}`} data-testid={`proof-card-${card.id}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{card.label}</p>
            <p className="mt-2 text-2xl font-black">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-template-status">
          <h4 className="text-lg font-bold text-slate-900">WhatsApp Template Status</h4>
          <div className="mt-3 rounded-xl bg-slate-50 p-4 text-sm">
            <p className="font-semibold text-slate-800">school_login_credentials</p>
            <p className="mt-1 text-slate-500">Status: {data.templates?.school_login_credentials?.status || 'Unknown'}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-cta-feed">
          <h4 className="text-lg font-bold text-slate-900">Recent CTA Events</h4>
          <div className="mt-3 space-y-2">
            {(data.recent_ctas || []).slice(0, 5).map((item, idx) => (
              <div key={idx} className="rounded-xl bg-slate-50 p-3 text-sm">
                <p className="font-semibold text-slate-800">{item.cta_label}</p>
                <p className="text-xs text-slate-500">{item.source_page} → {item.destination}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-source-breakdown">
          <h4 className="text-lg font-bold text-slate-900">CTA Source Breakdown</h4>
          <div className="mt-3 space-y-2">
            {(data.analytics?.source_breakdown || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-700">{item._id || 'unknown'}</span>
                <span className="font-bold text-orange-600">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-destination-breakdown">
          <h4 className="text-lg font-bold text-slate-900">Destination Breakdown</h4>
          <div className="mt-3 space-y-2">
            {(data.analytics?.destination_breakdown || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-700">{item._id || 'unknown'}</span>
                <span className="font-bold text-blue-600">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-stage-breakdown">
          <h4 className="text-lg font-bold text-slate-900">Identity Stage Breakdown</h4>
          <div className="mt-3 space-y-2">
            {(data.analytics?.stage_breakdown || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-700">{item._id || 'unknown'}</span>
                <span className="font-bold text-emerald-600">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-email-feed">
          <h4 className="text-lg font-bold text-slate-900">Recent Email Proof</h4>
          <div className="mt-3 space-y-2">
            {(data.recent_email_queue || []).slice(0, 5).map((item, idx) => (
              <div key={item.queue_id || idx} className="rounded-xl border border-slate-100 p-3 text-sm flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{item.email}</p>
                  <p className="text-xs text-slate-500">{item.type} · {item.status}</p>
                  <p className="text-xs text-slate-400 truncate">{item.lead_profile?.school_name || 'No school'}</p>
                </div>
                <ResendButton kind="email" queueId={item.queue_id} onDone={onRefresh} language={language} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-whatsapp-feed">
          <h4 className="text-lg font-bold text-slate-900">Recent WhatsApp Proof</h4>
          <div className="mt-3 space-y-2">
            {(data.recent_whatsapp_queue || []).slice(0, 5).map((item, idx) => (
              <div key={item.id || item.queue_id || idx} className="rounded-xl border border-slate-100 p-3 text-sm flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{item.phone || '—'}</p>
                  <p className="text-xs text-slate-500">{item.template_name || item.type} · {item.status}</p>
                  <p className="text-xs text-slate-400 truncate">{item.lead_profile?.school_name || 'No school'}</p>
                </div>
                {item.phone && item.template_name ? (
                  <ResendButton kind="whatsapp" queueId={item.id || item.queue_id} onDone={onRefresh} language={language} />
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 self-center" data-testid="resend-legacy-tag">
                    {language === 'hi' ? 'Legacy' : 'Legacy'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conversion-proof-identity-feed">
        <h4 className="text-lg font-bold text-slate-900">Recent Identity Profiles</h4>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {(data.recent_identities || []).slice(0, 6).map((item, idx) => (
            <div key={idx} className="rounded-xl border border-slate-100 p-4 text-sm">
              <p className="font-semibold text-slate-900">{item.principal_name || 'Unknown Lead'}</p>
              <p className="mt-1 text-slate-500">{item.school_name || 'No school'}</p>
              <p className="mt-2 text-xs text-slate-500">{item.phone || item.email || 'No identity'}</p>
              <p className="mt-2 inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{item.followup_stage || 'discovery'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
